import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatEuro, formatDate } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const KPIS = [
  { key: "demandes_mois", label: "Demandes ce mois" },
  { key: "demandes_nouvelles", label: "Demandes en attente" },
  { key: "devis_envoye", label: "Devis envoyés" },
  { key: "devis_accepte_mois", label: "Devis acceptés (mois)" },
  { key: "factures_impayees", label: "Factures impayées" },
  { key: "ca_ht", label: "CA HT encaissé", currency: true },
];

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/dashboard")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-eyebrow mb-3">Tableau de bord</p>
          <h1 className="h-display text-4xl md:text-5xl">Vue d'ensemble.</h1>
        </div>
        <p className="text-serial">
          {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
        </p>
      </div>

      {loading || !data ? (
        <p className="text-serial">Chargement</p>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border mb-12" data-testid="dashboard-kpis">
            {KPIS.map((k) => (
              <div key={k.key} className="bg-background p-5 md:p-7 min-h-[140px] flex flex-col justify-between" data-testid={`kpi-${k.key}`}>
                <span className="text-serial">{k.label}</span>
                <p className="font-serif text-4xl md:text-5xl leading-none">
                  {k.currency ? formatEuro(data[k.key]) : data[k.key] ?? 0}
                </p>
              </div>
            ))}
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div data-testid="recent-demandes">
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-serif text-2xl">Dernières demandes</h2>
                <Link to="/admin/demandes" className="text-eyebrow link-underline-static">
                  Toutes
                </Link>
              </div>
              <ul className="border-t border-border">
                {data.recent_demandes?.length === 0 && (
                  <li className="py-6 text-muted-foreground text-[14px]">Aucune demande pour le moment.</li>
                )}
                {data.recent_demandes?.map((d) => (
                  <li key={d.id} className="border-b border-border py-4 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg leading-tight">{d.nom}</p>
                      <p className="text-serial mt-1">
                        {d.cible} · {formatDate(d.created_at)}
                      </p>
                    </div>
                    <span className={`text-eyebrow ${d.status === "nouvelle" ? "text-primary" : "text-muted-foreground"}`}>
                      {d.status}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div data-testid="recent-devis">
              <div className="flex items-end justify-between mb-5">
                <h2 className="font-serif text-2xl">Derniers devis</h2>
                <Link to="/admin/devis" className="text-eyebrow link-underline-static">
                  Tous
                </Link>
              </div>
              <ul className="border-t border-border">
                {data.recent_devis?.length === 0 && (
                  <li className="py-6 text-muted-foreground text-[14px]">Aucun devis pour le moment.</li>
                )}
                {data.recent_devis?.map((d) => (
                  <li key={d.id} className="border-b border-border py-4 flex items-start justify-between gap-4">
                    <div>
                      <p className="font-serif text-lg leading-tight">{d.titre}</p>
                      <p className="text-serial mt-1">
                        {d.numero} · {formatDate(d.created_at)}
                      </p>
                    </div>
                    <span className="text-[14px] font-medium">{formatEuro(d.total_ttc)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-16">
            <Link to="/admin/devis/nouveau" className="btn-terra" data-testid="dashboard-cta-devis">
              Nouveau devis <ArrowUpRight size={14} />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
